import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Copy, CheckCircle2, XCircle, Loader2, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API_BASE } from '@/utils/api';
import { deviceId } from '@/utils/presence';

const HitRow = ({ h, i }) => (
  <div className="p-3 rounded-lg bg-muted text-xs space-y-1" data-testid={`ip-hit-${i}`}>
    <div className="flex justify-between gap-2">
      <span className="font-mono font-bold text-sm" data-testid={`ip-hit-ip-${i}`}>{h.ip}</span>
      <span className="text-muted-foreground">{new Date(h.at).toLocaleTimeString('es-AR')}</span>
    </div>
    <div className="text-muted-foreground truncate">Equipo {h.device_id.slice(0, 8)} · {h.user_agent}</div>
    <div className="font-mono text-[11px] break-all text-muted-foreground">Conexión desde {h.peer} · cadena: {h.forwarded_for || '(vacía)'}</div>
  </div>
);

const Verdict = ({ data }) => (
  <div className={`flex items-center gap-2 p-3 rounded-lg font-semibold ${data.passed ? 'bg-emerald-500/15 text-emerald-600' : 'bg-amber-500/15 text-amber-700'}`} data-testid="ip-test-verdict">
    {data.passed ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
    {data.passed
      ? `Prueba superada: ${data.devices} equipos con ${data.distinct_ips} IPs distintas`
      : `Esperando: ${data.devices} equipo(s), ${data.distinct_ips} IP(s) distinta(s). Hacen falta 2 equipos con IPs distintas.`}
  </div>
);

export default function IpDiagnostics() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);
  const link = token ? `${window.location.origin}/prueba-ip/${token}` : '';

  const create = async () => {
    const { data: d } = await axios.post(`${API_BASE}/diagnostics/ip-tests`);
    setToken(d.token);
    setData(null);
  };
  const load = useCallback(async () => {
    if (!token) return;
    const { data: d } = await axios.get(`${API_BASE}/diagnostics/ip-tests/${token}`);
    setData(d);
  }, [token]);
  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => navigate('/settings')} data-testid="ip-diag-back"><ArrowLeft className="w-4 h-4 mr-2" />Configuración</Button>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Wifi className="w-5 h-5" />Diagnóstico de IP</CardTitle>
            <CardDescription>
              Comprueba que el servidor ve la IP real de cada cliente (se usa para frenar abusos). Abrí el link en dos celulares:
              uno con wifi y otro con datos móviles. Deberían aparecer dos IPs distintas.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={create} data-testid="ip-test-create-btn">{token ? 'Crear otra prueba' : 'Crear prueba'}</Button>
            {token && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <code className="flex-1 p-2 rounded bg-muted text-xs break-all" data-testid="ip-test-link">{link}</code>
                  <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(link); toast.success('Link copiado'); }} data-testid="ip-test-copy-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">El link vence en 30 minutos.</p>
              </div>
            )}
            {data && <Verdict data={data} />}
            {data?.hits?.map((h, i) => <HitRow key={`${h.device_id}-${h.at}`} h={h} i={i} />)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function IpTestVisit() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    axios.post(`${API_BASE}/public/ip-test/${token}`, { device_id: deviceId() })
      .then(r => setState({ ip: r.data.ip }))
      .catch(e => setState({ error: e.response?.data?.detail || 'No se pudo registrar' }));
  }, [token]);
  return (
    <div className="min-h-screen grid place-items-center p-6 bg-background">
      <Card className="max-w-sm w-full text-center">
        <CardContent className="pt-6 space-y-3">
          {state.loading && <Loader2 className="w-8 h-8 animate-spin mx-auto" />}
          {state.ip && (<>
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
            <p className="font-semibold">Listo, ya quedó registrado</p>
            <p className="text-sm text-muted-foreground">Tu IP según el servidor:</p>
            <p className="font-mono text-lg font-bold" data-testid="ip-visit-ip">{state.ip}</p>
          </>)}
          {state.error && <p className="text-destructive" data-testid="ip-visit-error">{state.error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
